import * as PIXI from 'pixi.js';
import { CanvasContext, PixiAppRefs } from './types';
import { WireBundleSegment } from '../../types';
import { HarnessNode } from '../../entities/HarnessNode';
import { Connector } from '../../entities/Connector';
import { buildWireContextMenu } from './wireMenu';
import { buildConnectorContextMenu } from './connectorMenu';
import { createMarqueeController } from './marquee';

/** Finds the wire section (if any) at a world point, across all rendered bundles. */
function findSectionAt(refs: PixiAppRefs, worldX: number, worldY: number): WireBundleSegment | null {
  const bundles = Array.from(refs.wireBundleEntities.current.values());
  for (const bundle of bundles) {
    const segment = bundle.getSegmentAt(worldX, worldY);
    if (segment) {
      return segment;
    }
  }
  return null;
}

/** Finds the connector card (if any) at a world point. */
function findConnectorAt(refs: PixiAppRefs, worldX: number, worldY: number): Connector | null {
  const connectors = Array.from(refs.connectorEntities.current.values());
  for (const connector of connectors) {
    if (connector.containsPoint(worldX, worldY)) {
      return connector;
    }
  }
  return null;
}

/** Finds the harness node (if any) at a world point. */
function findNodeAt(refs: PixiAppRefs, worldX: number, worldY: number): HarnessNode | null {
  const nodes = Array.from(refs.nodeEntities.current.values());
  for (const node of nodes) {
    if (node.isPointInside(worldX, worldY)) {
      return node;
    }
  }
  return null;
}

/** Registers the interaction-manager callbacks that bridge input to viewport and wire creation. */
export function setupInteractionCallbacks(ctx: CanvasContext): void {
  const { refs, stateManager, contextMenuHandlers, handleWireCreationClick, handleWireCreationCancel } = ctx;
  const marquee = createMarqueeController(refs, stateManager);

  refs.interactionManager.current!.setCallbacks({
    onSelectStart: marquee.onSelectStart,
    onSelectMove: marquee.onSelectMove,
    onSelectEnd: marquee.onSelectEnd,
    onSelectClear: marquee.onSelectClear,
    onPan: (deltaX, deltaY) => {
      refs.viewportManager.current?.pan(deltaX, deltaY);
    },
    onZoom: (mouseX, mouseY, wheelDelta) => {
      refs.viewportManager.current?.zoom(mouseX, mouseY, wheelDelta);
    },
    onContextMenu: (x, y) => {
      if (refs.mounted.current && refs.viewportManager.current) {
        // x/y are client coordinates (used to place the popup). screenToWorld
        // expects canvas-relative coordinates, so subtract the canvas offset.
        const canvas = refs.appManager.current?.getApp()?.canvas as HTMLCanvasElement | undefined;
        const rect = canvas?.getBoundingClientRect();
        const canvasX = rect ? x - rect.left : x;
        const canvasY = rect ? y - rect.top : y;
        const worldCoords = refs.viewportManager.current.screenToWorld(canvasX, canvasY);

        // A right-click on a connector card offers wire actions when it lands on
        // a wire row, otherwise the connector's own actions. Resolved here (like
        // nodes and sections below) because the native context-menu event is the
        // one that reliably reaches the canvas.
        const connector = findConnectorAt(refs, worldCoords.x, worldCoords.y);
        if (connector) {
          const wireId = connector.wireAt(worldCoords.x, worldCoords.y);
          const items = wireId
            ? buildWireContextMenu({ stateManager, contextMenuHandlers }, wireId, x, y)
            : buildConnectorContextMenu({ stateManager, contextMenuHandlers }, connector.getData(), x, y);
          if (items.length > 0) {
            contextMenuHandlers.showContextMenu(x, y, items);
          }
          return;
        }

        // A right-click on a harness node deletes it; nodes take priority over
        // the sections meeting at them. Resolved here (rather than on the node)
        // because the native context-menu event is the one that reliably reaches
        // the canvas.
        const node = findNodeAt(refs, worldCoords.x, worldCoords.y);
        if (node) {
          contextMenuHandlers.showContextMenu(x, y, [
            {
              label: 'Delete Node',
              action: () => contextMenuHandlers.handleNodeDelete(node.getData().id),
              dangerous: true
            }
          ]);
          return;
        }

        // A right-click on a wire section edits that section's length; elsewhere
        // it offers to add entities.
        const section = findSectionAt(refs, worldCoords.x, worldCoords.y);
        if (section) {
          contextMenuHandlers.showContextMenu(x, y, [
            {
              label: 'Section Properties',
              action: () => contextMenuHandlers.handleSectionEdit(section.aId, section.bId)
            }
          ]);
          return;
        }

        const selection = stateManager.getSelection();
        const selectionItems = selection.length > 0
          ? [{
              label: `Delete selected (${selection.length})`,
              action: () => stateManager.deleteElements(selection),
              dangerous: true
            }]
          : [];

        contextMenuHandlers.showContextMenu(x, y, [
          ...selectionItems,
          {
            label: 'Add Connector',
            action: () => contextMenuHandlers.handleAddConnector(worldCoords.x, worldCoords.y)
          },
          {
            label: 'Add Harness Node',
            action: () => contextMenuHandlers.handleAddNode(worldCoords.x, worldCoords.y)
          }
        ]);
      }
    },
    onWireCreationMove: (screenX, screenY) => {
      if (refs.mounted.current && refs.viewportManager.current && refs.wirePreview.current) {
        const worldCoords = refs.viewportManager.current.screenToWorld(screenX, screenY);
        refs.wirePreview.current.updateCurrentPosition(worldCoords.x, worldCoords.y);
        stateManager.setPreviewWire({
          ...stateManager.getInteractionState().previewWire!,
          currentX: worldCoords.x,
          currentY: worldCoords.y
        });
      }
    },
    onWireCreationClick: (screenX, screenY) => {
      if (refs.mounted.current && refs.viewportManager.current) {
        const worldCoords = refs.viewportManager.current.screenToWorld(screenX, screenY);
        handleWireCreationClick(worldCoords.x, worldCoords.y);
      }
    },
    onWireCreationCancel: () => {
      handleWireCreationCancel();
    }
  });
}

/** Attaches the per-frame ticker callback, guarding against duplicate registration. */
export function setupTicker(app: PIXI.Application, isNewInstance: boolean, ctx: CanvasContext): void {
  const { refs, updateApplication: onTick } = ctx;

  if (app.ticker && (!refs.tickerCallbackAttached.current || isNewInstance)) {
    if (refs.tickerCallbackAttached.current) {
      app.ticker.remove(onTick);
    }
    app.ticker.add(onTick);
    refs.tickerCallbackAttached.current = true;

    if (isNewInstance) {
      refs.appManager.current!.startTicker();
    }
    return;
  }

  if (!refs.tickerCallbackAttached.current && !app.ticker) {
    throw new Error('Pixi ticker is not available');
  }
}
